import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowDropDown, Search, Store } from "@mui/icons-material";
import type { Company } from "@/api/company.service";

// Sem acento e sem caixa, pra busca achar "São Paulo" digitando "sao".
const normalizar = (s: string) => s.normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase();

// Seletor das lojas da ruptura: todas marcadas por padrão, desmarcar tira a loja da tela.
// ponytail: por enquanto só a tela; ainda não filtra as buscas (as queries aceitam uma loja só).
export function SeletorLojas({
  lojas,
  desmarcadas,
  onChange,
}: {
  lojas: Company[];
  // Códigos (erp_code) das lojas tiradas; vazio = todas.
  desmarcadas: Set<string>;
  onChange: (desmarcadas: Set<string>) => void;
}) {
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const [busca, setBusca] = useState("");
  const termo = normalizar(busca.trim());
  // Com busca, "Selecionar todas"/"Limpar" valem só pras lojas que aparecem.
  const visiveis = termo ? lojas.filter((l) => normalizar(`${l.name} ${l.erp_code}`).includes(termo)) : lojas;
  const marcadas = lojas.length - lojas.filter((l) => desmarcadas.has(l.erp_code)).length;
  const rotulo = marcadas === lojas.length ? "Lojas (todas)" : `Lojas (${marcadas} de ${lojas.length})`;

  const alternar = (codigo: string) => {
    const novo = new Set(desmarcadas);
    if (!novo.delete(codigo)) novo.add(codigo);
    onChange(novo);
  };

  const marcarVisiveis = (marcar: boolean) => {
    const novo = new Set(desmarcadas);
    for (const l of visiveis) {
      if (marcar) novo.delete(l.erp_code);
      else novo.add(l.erp_code);
    }
    onChange(novo);
  };

  return (
    <>
      <Button
        size="small"
        variant={desmarcadas.size > 0 ? "contained" : "outlined"}
        startIcon={<Store fontSize="small" />}
        endIcon={<ArrowDropDown />}
        onClick={(e) => setAncora(e.currentTarget)}
        disabled={lojas.length === 0}
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {rotulo}
      </Button>
      <Popover
        open={!!ancora}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        slotProps={{ transition: { onExited: () => setBusca("") } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 1.5, width: 280, maxHeight: 420, display: "flex", flexDirection: "column" }}>
          <TextField
            size="small"
            autoFocus
            placeholder="Buscar loja..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ my: 0.5 }}>
            <Button size="small" onClick={() => marcarVisiveis(true)} disabled={visiveis.length === 0}>
              Selecionar todas
            </Button>
            <Button size="small" onClick={() => marcarVisiveis(false)} disabled={visiveis.length === 0}>
              Limpar
            </Button>
          </Stack>
          <Divider sx={{ mb: 0.5 }} />
          <Stack sx={{ overflowY: "auto" }}>
            {visiveis.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: "center" }}>
                Nenhuma loja encontrada
              </Typography>
            )}
            {visiveis.map((l) => (
              <FormControlLabel
                key={l.erp_code}
                label={l.name}
                control={<Checkbox size="small" checked={!desmarcadas.has(l.erp_code)} onChange={() => alternar(l.erp_code)} />}
                slotProps={{ typography: { variant: "body2" } }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
